import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './WorkshopBookingList.css';

function WorkshopBookingList() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterLocation, setFilterLocation] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [expandedBooking, setExpandedBooking] = useState(null);
  const searchTimeout = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchBookings();
  }, [filterStatus, filterLocation, dateFrom, dateTo]);

  useEffect(() => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      fetchBookings();
    }, 400);
    return () => {
      if (searchTimeout.current) clearTimeout(searchTimeout.current);
    };
  }, [searchQuery]);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const params = {};
      if (searchQuery.trim()) params.search = searchQuery.trim();
      if (filterStatus) params.status = filterStatus;
      if (filterLocation) params.location = filterLocation;
      if (dateFrom) params.startDate = dateFrom;
      if (dateTo) params.endDate = dateTo;

      const response = await axios.get('/api/workshops/bookings', { params });
      setBookings(response.data || []);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch bookings:', err);
      setError('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const toggleExpand = (bookingId) => {
    setExpandedBooking(expandedBooking === bookingId ? null : bookingId);
  };

  if (loading && bookings.length === 0) {
    return (
      <div className="workshop-booking-list">
        <div className="ws-loading">
          <div className="spinner"></div>
          <p>Loading bookings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="workshop-booking-list">
      <div className="ws-page-header">
        <h1>Workshop Bookings</h1>
        <button className="ws-btn-primary" onClick={() => navigate('/workshops/bookings/new')}>
          New Booking
        </button>
      </div>

      <div className="ws-filters">
        <div className="ws-filter-group ws-search-group">
          <label>Search</label>
          <input
            type="text"
            placeholder="Search name, email, order number..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="ws-filter-group">
          <label>Status</label>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="">All Statuses</option>
            <option value="confirmed">Confirmed</option>
            <option value="pending">Pending</option>
            <option value="cancelled">Cancelled</option>
            <option value="checked_in">Checked In</option>
            <option value="no_show">No Show</option>
          </select>
        </div>
        <div className="ws-filter-group">
          <label>Location</label>
          <select value={filterLocation} onChange={(e) => setFilterLocation(e.target.value)}>
            <option value="">All Locations</option>
            <option value="charleston">Charleston</option>
            <option value="boston">Boston</option>
            <option value="atlanta">Atlanta</option>
          </select>
        </div>
        <div className="ws-filter-group">
          <label>From</label>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
        </div>
        <div className="ws-filter-group">
          <label>To</label>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
        </div>
        {(searchQuery || filterStatus || filterLocation || dateFrom || dateTo) && (
          <button
            className="ws-btn-secondary ws-btn-sm"
            onClick={() => {
              setSearchQuery('');
              setFilterStatus('');
              setFilterLocation('');
              setDateFrom('');
              setDateTo('');
            }}
          >
            Clear
          </button>
        )}
      </div>

      {error && <div className="ws-error">{error}</div>}

      <div className="ws-section">
        {bookings.length === 0 ? (
          <div className="ws-empty">No bookings found matching your filters</div>
        ) : (
          <div className="ws-table-container">
            <table className="ws-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Customer</th>
                  <th>Email</th>
                  <th>Session</th>
                  <th>Attendees</th>
                  <th>Status</th>
                  <th>Source</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {bookings.map((booking) => (
                  <React.Fragment key={booking.id}>
                    <tr
                      className="ws-clickable-row"
                      onClick={() => toggleExpand(booking.id)}
                    >
                      <td>{formatDate(booking.session_date)}</td>
                      <td className="ws-text-bold">{booking.customer_name}</td>
                      <td>{booking.customer_email}</td>
                      <td>{booking.workshop_type} - {booking.location_name}</td>
                      <td>{booking.attendee_count || 1}</td>
                      <td>
                        <span className={`ws-badge ws-badge-${booking.status}`}>
                          {booking.status}
                        </span>
                      </td>
                      <td>
                        <span className="ws-source-tag">{booking.source || 'manual'}</span>
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <button
                          className="ws-btn-secondary ws-btn-sm"
                          onClick={() => navigate(`/workshops/sessions/${booking.session_id}`)}
                        >
                          View Session
                        </button>
                      </td>
                    </tr>
                    {expandedBooking === booking.id && (
                      <tr className="ws-expanded-row">
                        <td colSpan="8">
                          <div className="ws-expanded-content">
                            <div className="ws-expanded-grid">
                              <div className="ws-expanded-item">
                                <span className="ws-expanded-label">Phone</span>
                                <span>{booking.customer_phone || 'N/A'}</span>
                              </div>
                              <div className="ws-expanded-item">
                                <span className="ws-expanded-label">Order Number</span>
                                <span>{booking.shopify_order_number || booking.external_order_id || 'N/A'}</span>
                              </div>
                              <div className="ws-expanded-item">
                                <span className="ws-expanded-label">Booked At</span>
                                <span>{formatDate(booking.created_at)}</span>
                              </div>
                              <div className="ws-expanded-item">
                                <span className="ws-expanded-label">Checked In</span>
                                <span>{booking.checked_in_at ? formatDate(booking.checked_in_at) : 'Not yet'}</span>
                              </div>
                              {booking.special_requests && (
                                <div className="ws-expanded-item ws-expanded-full">
                                  <span className="ws-expanded-label">Special Requests</span>
                                  <span>{booking.special_requests}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default WorkshopBookingList;
